import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, TrendingUp, ArrowUp, ArrowDown } from "lucide-react";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const mLabel = (mo) => new Date(`${mo}-01T00:00:00`).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
const tone = (v) => (v == null ? "" : v >= 9 ? "text-emerald-600" : v >= 8 ? "text-foreground" : v >= 7 ? "text-amber-600" : "text-red-600");

const TrendCell = ({ c }) => (
  <td className="border-l border-border px-2 py-1.5 text-center" title={`${c.count} review(s)${c.ly_avg != null ? ` · last year ${c.ly_avg} (${c.ly_count})` : ""}`}>
    {c.avg == null ? <span className="text-muted-foreground">—</span> : (
      <div>
        <span className={`font-bold tabular-nums ${tone(c.avg)}`}>{c.avg.toFixed(1)}</span>
        <span className="ml-0.5 text-[0.6rem] text-muted-foreground">({c.count})</span>
      </div>
    )}
    {c.change != null && (
      <div className={`inline-flex items-center text-[0.65rem] font-semibold ${c.change > 0 ? "text-emerald-600" : c.change < 0 ? "text-red-600" : "text-muted-foreground"}`}>
        {c.change > 0 ? <ArrowUp className="h-3 w-3" /> : c.change < 0 ? <ArrowDown className="h-3 w-3" /> : null}{Math.abs(c.change).toFixed(1)} vs LY
      </div>
    )}
    {c.change == null && c.ly_avg != null && <div className="text-[0.6rem] text-muted-foreground">LY {c.ly_avg.toFixed(1)}</div>}
  </td>
);

const Row = ({ r, bold }) => (
  <tr className={`border-b border-border ${bold ? "bg-secondary/50 font-semibold" : ""}`} data-testid={`trend-row-${r.key}`}>
    <td className="sticky left-0 min-w-[150px] bg-card px-3 py-1.5 text-sm font-semibold">{r.label}</td>
    {r.cells.map((c) => <TrendCell key={c.month} c={c} />)}
  </tr>
);

export const ScoreTrends = ({ propertyId }) => {
  const [scope, setScope] = useState("property");
  const [end, setEnd] = useState(thisMonth());
  const [data, setData] = useState(null);
  const pid = scope === "all" ? "all" : propertyId;
  useEffect(() => {
    if (!pid || !end) return;
    setData(null);
    api.get("/guest-comments/trends", { params: { property_id: pid, end_month: end } }).then((r) => setData(r.data)).catch(() => setData({ months: [], rows: [] }));
  }, [pid, end]);
  return (
    <Card className="rounded-none border" data-testid="score-trends">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-base font-bold"><TrendingUp className="h-4 w-4 text-primary" /> Score Trends</h2>
          <p className="text-xs text-muted-foreground">Average guest score out of 10 by month (number of reviews in brackets), with change vs the same month last year.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-border">
            <Button size="sm" variant={scope === "property" ? "default" : "ghost"} className="rounded-none" onClick={() => setScope("property")} data-testid="trend-scope-property">This property</Button>
            <Button size="sm" variant={scope === "all" ? "default" : "ghost"} className="rounded-none" onClick={() => setScope("all")} data-testid="trend-scope-all">All properties</Button>
          </div>
          <Input type="month" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-40" data-testid="trend-end-month" />
        </div>
      </div>
      {!data ? <div className="flex h-32 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="trend-table">
            <thead><tr className="border-b border-border">
              <th className="sticky left-0 bg-card px-3 py-2 text-left">{data.mode === "properties" ? "Property" : "Site"}</th>
              {data.months.map((m) => <th key={m} className="border-l border-border px-2 py-2 text-center font-semibold">{mLabel(m)}</th>)}
            </tr></thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={13} className="p-6 text-center text-muted-foreground">No scored reviews in this period.</td></tr>}
              {data.rows.map((r) => <Row key={r.key} r={r} />)}
              {data.rows.length > 1 && data.total && <Row r={data.total} bold />}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};
